import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";

type Pillar = {
  key: "work" | "stay" | "play";
  label: string;
  color: string;
  image: string;
  timeOfDay: string;
  caption: string;
  description: string;
  tags: string[];
  imageSide: "left" | "right";
  background: string;
};

const PILLARS: Pillar[] = [
  {
    key: "work",
    label: "Work",
    color: "#017bef",
    image: "/figma-assets/home/pillar-work.jpg",
    timeOfDay: "Morning",
    caption: "Find the right space to get things done.",
    description:
      "Modern life doesn't move in straight lines. The same day can call for a productive morning at a coworking desk — focused, connected, professional.",
    tags: ["Coworking spaces", "Dedicated desks", "Meeting rooms", "Conference rooms", "Private offices"],
    imageSide: "left",
    background: "bg-white",
  },
  {
    key: "stay",
    label: "Stay",
    color: "#0062c0",
    image: "/figma-assets/home/pillar-stay.jpg",
    timeOfDay: "Evening",
    caption: "Find a place that feels right, wherever you're headed.",
    description:
      "Then an overnight stay in a new city. Finding, booking and paying for each of these used to mean a different platform, a different account and a different process every time.",
    tags: ["Hotels", "Apartments", "Boutique properties"],
    imageSide: "right",
    background: "bg-[#f8fbff]",
  },
  {
    key: "play",
    label: "Play",
    color: "#0ea5e9",
    image: "/figma-assets/home/pillar-play.jpg",
    timeOfDay: "Later",
    caption: "Make the most of your time away from the desk.",
    description:
      "SPACIA removes that friction, bringing Work, Stay and Play together on one platform so you can discover, book and manage every space you need without switching between apps.",
    tags: ["Wellness", "Dining", "Adventure", "Events", "Concierge"],
    imageSide: "left",
    background: "bg-white",
  },
];

function PillarRow({ pillar }: { pillar: Pillar }) {
  const history = useHistory();
  const { key, label, color } = pillar;

  const image = (
    <button
      type="button"
      onClick={() => history.push(`/${key}`)}
      className="relative block h-full min-h-[320px] w-full overflow-hidden rounded-3xl text-left sm:min-h-[480px]"
    >
      <img src={pillar.image} alt={label} className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent via-50% to-transparent" />
      <span
        className="absolute left-6 top-6 rounded-full px-[16px] py-[5px] text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white"
        style={{ backgroundColor: color }}
      >
        {pillar.timeOfDay}
      </span>
      <div className="absolute inset-x-6 bottom-6 flex items-center justify-between gap-[16px] rounded-2xl border-[1px] border-solid border-white/50 bg-white/95 p-[16px] shadow-[0_4px_24px_0_rgba(0,0,0,0.1)]">
        <div className="text-[12px] leading-4">
          <p className="font-bold text-[#0a0a0a]">{label}</p>
          <p className="pt-0.5 text-[#717182]">{pillar.caption}</p>
        </div>
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: color }}
        >
          <ArrowRight className="size-3.5" />
        </span>
      </div>
    </button>
  );

  const text = (
    <div className="flex h-full flex-col justify-center py-10 lg:px-8 lg:py-20">
      <p className="pb-[16px] text-[12px] font-bold uppercase leading-4 tracking-[1.2px]" style={{ color }}>
        {label}
      </p>
      <h3 className="pb-[16px] text-[40px] font-bold leading-[48px] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px]">
        {label}
      </h3>
      <p className="max-w-[384px] pb-8 text-[16px] leading-[26px] text-[#717182]">{pillar.description}</p>
      <div className="flex flex-wrap gap-2 pb-8">
        {pillar.tags.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => history.push(`/workspaces-search?type=${key}&query=${encodeURIComponent(tag)}`)}
            className="rounded-full border-[1px] border-solid px-[12px] py-1.5 text-[14px] font-semibold leading-5 transition-opacity hover:opacity-80"
            style={{ color, borderColor: `${color}30`, backgroundColor: `${color}08` }}
          >
            {tag}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => history.push(`/${key}`)}
        className="inline-flex items-center gap-2 self-start text-[14px] font-semibold leading-5 hover:underline"
        style={{ color }}
      >
        Explore {label}
        <ArrowRight className="size-[15px]" />
      </button>
    </div>
  );

  return (
    <div className={pillar.background}>
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 px-[16px] sm:px-8 lg:grid-cols-2">
        {/* Image comes first on mobile; on desktop it swaps sides per pillar */}
        <div className={pillar.imageSide === "right" ? "lg:col-start-2 lg:row-start-1" : ""}>{image}</div>
        <div className={pillar.imageSide === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>{text}</div>
      </div>
    </div>
  );
}

function ScrollCue() {
  return (
    <div className="flex justify-center bg-white py-[16px]">
      <span className="flex size-8 items-center justify-center rounded-full border-[1px] border-solid border-[#e5e7eb] text-[#717182]">
        <ArrowDown className="size-3.5" />
      </span>
    </div>
  );
}

export default function PillarsSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1440px] px-[16px] py-20 sm:px-8">
        <div className="max-w-[672px]">
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">The Platform</p>
          <h2 className="pt-[16px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[60px]">
            The Way We Work, Stay, <br className="hidden sm:block" />
            and Play Is Changing
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            SPACIA is the all-in-one platform connecting you to flexible workspaces, quality accommodation, and curated
            lifestyle experiences. Through three pillars — Work, Stay and Play — you can reserve a coworking desk, a
            hotel room, and a dinner table without ever leaving the app.
          </p>
        </div>
      </div>

      {PILLARS.map((pillar, index) => (
        <React.Fragment key={pillar.key}>
          <PillarRow pillar={pillar} />
          {index < PILLARS.length - 1 && <ScrollCue />}
        </React.Fragment>
      ))}
    </section>
  );
}
